/**
 * `verify-fields` — CLM-86 (R-86-1). FR-07, CR-01, NFR-06, NFR-07.
 *
 * Mass Confirm / mass Reject over the Verification queue. One request carries up
 * to {@link BULK_ITEMS_MAX} queue rows; the API settles **each** of them through
 * the same single-field settle `verify-field` runs, in its own transaction, and
 * writes ONE `HumanVerification` audit row per settled field (CLAUDE.md rule 7).
 * There is no summary row — `ck_audit_log_field_attributable` (0040) makes a row
 * covering a whole document unrepresentable, and this endpoint leans on that.
 *
 * Every item carries `verification_id` and `seen_extracted_value` so the
 * stale-value refusal of `verify-field` applies per row (D-86-6): a bulk
 * attestation to a value nobody read is the failure FR-07 exists to prevent.
 * Only `confirm` and `reject` — `correct` needs a per-row value and
 * `accept_extracted` is one field's Legal verb; both stay single-row (D-86-4).
 */
import { z } from 'zod';
import { ContractKeySchema, IsoTimestampUtcSchema, OpaqueIdSchema } from '../primitives.js';
import { AuditReceiptSchema } from '../provenance.js';
import { FieldNameSchema } from '../domain/verification.js';
import { DataTrustSchema, ProcessingStatusSchema } from '../vocabulary.js';
import { COUNTERPARTY_NAME_FIELD } from './register-vendor-party.js';
/** The most rows one bulk call may settle. The queue page is smaller than this. */
export const BULK_ITEMS_MAX = 200;
/** Per-item outcome of a bulk call. `settled` is the only one that wrote anything. */
export const BULK_ITEM_STATUSES = ['settled', 'not_permitted', 'not_pending', 'not_found', 'failed'];
/**
 * The field paths a queue row may carry — the eighteen the extraction orchestrator
 * verifies plus the counterparty-name row CLM-78 adds. The queue's Field filter
 * lists exactly these (D-86-5).
 */
export const QUEUE_FIELD_NAMES = [
    'dates.effective_date',
    'dates.execution_date',
    'dates.expiry_date',
    'dates.termination_date',
    'dates.renewal_notice_date',
    'dates.renewal_notice_days',
    'dates.auto_renew',
    'key_terms.contract_value',
    'key_terms.currency',
    'key_terms.payment_terms',
    'key_terms.liability_cap',
    'key_terms.indemnity_scope',
    'key_terms.insurance_requirements',
    'key_terms.governing_law',
    'key_terms.jurisdiction',
    'key_terms.exclusivity',
    'plain_summary',
    'personal_data.contains_personal_data',
    COUNTERPARTY_NAME_FIELD,
];
export const VerifyFieldsBulkItemSchema = z
    .object({
    verification_id: OpaqueIdSchema,
    contract_key: ContractKeySchema,
    field_name: FieldNameSchema,
    /** What the queue row showed — the same optimistic-concurrency check as verify-field. */
    seen_extracted_value: z.string().max(400).nullable(),
})
    .strict();
export const VerifyFieldsBulkRequestSchema = z
    .object({
    items: z.array(VerifyFieldsBulkItemSchema).min(1).max(BULK_ITEMS_MAX),
    outcome: z.enum(['confirm', 'reject']),
    /** One reason, applied to every rejected row. Required for reject. */
    reason: z.string().min(1).max(1000).optional(),
    note: z.string().min(1).max(1000).optional(),
})
    .strict()
    .superRefine((body, ctx) => {
    if (body.outcome === 'reject' && body.reason === undefined) {
        ctx.addIssue({ code: 'custom', path: ['reason'], message: 'reason is required for reject' });
    }
    if (body.outcome === 'confirm' && body.reason !== undefined) {
        ctx.addIssue({ code: 'custom', path: ['reason'], message: 'reason applies to reject only' });
    }
});
export const VerifyFieldsBulkResultSchema = z.object({
    verification_id: OpaqueIdSchema,
    contract_key: ContractKeySchema,
    field_name: FieldNameSchema,
    status: z.enum(BULK_ITEM_STATUSES),
    /** Present iff status = settled — the same AuditReceipt verify-field returns. */
    receipt: z
        .object({
        audit: AuditReceiptSchema,
        effective_value: z.string().max(400).nullable(),
        contract_processing_status: ProcessingStatusSchema,
        /** CLM-108 — as `VerifyFieldResponse.contract_data_trust`. */
        contract_data_trust: DataTrustSchema,
        remaining_pending_count: z.number().int().min(0),
    })
        .optional(),
    /** Present for failed only; never echoes a value. */
    message: z.string().max(200).optional(),
});
export const VerifyFieldsBulkResponseSchema = z.object({
    results: z.array(VerifyFieldsBulkResultSchema),
    counts: z.object({
        settled: z.number().int().min(0),
        not_permitted: z.number().int().min(0),
        not_pending: z.number().int().min(0),
        not_found: z.number().int().min(0),
        failed: z.number().int().min(0),
    }),
    verified_utc: IsoTimestampUtcSchema,
});
//# sourceMappingURL=verify-fields-bulk.js.map